<style scoped>
section.main-section {
  height: 100%;
}
.wrap-container {
  flex-wrap: nowrap;
  overflow-x: auto;
  display: flex;
}

.card-image {
  flex: 0 0 auto;
  max-height: 180px;
  max-width: 140px;
}
</style>

<template>
  <div class="main-wrapper">
    <section class="section main-section bg-gradient-default min-vh-100 pt-3">
      <main class="container card shadow mb-3">
        <b-overlay :show="loading" variant="white">
          <Header :title="entry ? entry.name : 'Loading...'" />
          <b-row>
            <b-col cols="6">
              <b-button-toolbar>
                <b-button-group>
                  <b-button variant="default" size="sm" @click="backToEntries">
                    <b-icon-chevron-left scale="1.5" />
                  </b-button>
                </b-button-group>
              </b-button-toolbar>
            </b-col>
            <b-col cols="6">
              <b-button-toolbar class="float-right">
                <b-button
                  variant="danger"
                  size="sm"
                  @click="confirmDelete"
                  v-if="isAdmin"
                >
                  <b-icon-trash size="1.3" />
                </b-button>
                <b-button
                  :variant="entry.completed ? 'warning' : 'primary'"
                  size="sm"
                  v-if="isAdmin"
                  @click="confirmCompleteChange"
                >
                  <b-icon-check-circle v-if="!entry.completed" />
                  <b-icon-x-circle v-else />
                </b-button>
                <b-button-group>
                  <b-dropdown variant="info" size="sm" right>
                    <template v-slot:button-content>
                      <b-icon-cloud-download scale="1.2" />
                    </template>
                    <b-dropdown-item @click="triggerDownloadAction('pdf')"
                      ><span> <b-icon-file-post /> PDF</span></b-dropdown-item
                    >
                    <b-dropdown-item @click="triggerDownloadAction('xls')"
                      ><span
                        ><b-icon-file-spreadsheet /> Excel</span
                      ></b-dropdown-item
                    >
                  </b-dropdown>
                </b-button-group>
              </b-button-toolbar>
            </b-col>
          </b-row>
          <div class="row mt-3">
            <b-col cols="12" class="mb-1">
              <b-badge :variant="entry.completed ? 'success' : 'warning'">
                <b-icon-exclamation-circle
                  v-if="!entry.completed"
                  scale="2.5"
                ></b-icon-exclamation-circle>
                <span class="ml-2" v-if="!entry.completed"
                  >La etrada no ha sido completada</span
                >
                <b-icon-check-circle
                  v-if="entry.completed"
                  scale="2.5"
                ></b-icon-check-circle>
                <span class="ml-2" v-if="entry.completed"
                  >La etrada ha sido completada exitosamente</span
                >
              </b-badge>
            </b-col>
            <b-col cols="12">
              <small>Entrada: {{ entry.entryId }}</small>
            </b-col>
            <b-col cols="12">
              <b-badge variant="primary" class="float-right"
                >Revision: {{ entryTemplateRevision }}</b-badge
              >
            </b-col>
            <b-col cols="12" class="mt-1">
              <p>Localidad: {{ entry.locality || "Sin registro" }}</p>
              <p>Creado por {{ entry.createdBy }}</p>
              <p>Creado el {{ entry.createdOn | timestamp }}</p>
              <p>
                <strong>Modificado</strong> el
                {{ entry.lastUpdatedAt | timestamp }}
              </p>
              <p>
                Tiempo total: <strong>{{ calculateDuration(entry) }}</strong>
              </p>
            </b-col>
          </div>
          <b-row class="my-2" v-if="mapMarkers.length > 0">
            <b-col cols="12">
              <p>Modificado en:</p>
            </b-col>
            <b-col>
              <GMap :config="mapConfig" :apiKey="mapApiKey">
                <template slot-scope="{ google, map }">
                  <GMarker
                    v-for="marker in mapMarkers"
                    :key="marker.id"
                    :google="google"
                    :map="map"
                    :marker="marker"
                  ></GMarker>
                </template>
              </GMap>
            </b-col>
          </b-row>
          <div class="row mb-3">
            <b-col cols="12" v-if="entry.images && entry.images.length > 0">
              <b-container fluid class="my-1">
                <b-row>
                  <b-col class="wrap-container">
                    <span v-for="img in entry.images" :key="img.name">
                      <b-img-lazy
                        v-if="img.url"
                        class="card-image p-1"
                        :src="img.url"
                      ></b-img-lazy>
                      <b-badge v-else>{{ img.name }}</b-badge>
                    </span>
                  </b-col>
                </b-row>
              </b-container>
            </b-col>
            <div class="col-12">
              <b-form-textarea
                v-model="entry.notes"
                max-rows="6"
                placeholder=""
                debounce="500"
                disabled
              ></b-form-textarea>
            </div>
          </div>
          <b-card no-body>
            <b-tabs card v-model="selectedTab">
              <b-tab
                v-for="(answers, category, indx) in categorizedQuestions"
                :key="category"
                class="pt-2"
                no-body
                lazy
              >
                <template v-slot:title>
                  {{ indx + 1 }}. {{ category }}
                </template>
                <b-list-group>
                  <b-list-group-item
                    v-for="(answer, key) in answers"
                    :key="key"
                    class="flex-column align-items-start"
                  >
                    <div class="row">
                      <div class="col-2">
                        <p>{{ answer.number }}.</p>
                      </div>
                      <div class="col-8">
                        <p class="text-left">{{ answer.name }}</p>
                      </div>
                      <div class="col-2">
                        <b-form-checkbox
                          class="float-right"
                          disabled
                          v-model="answer.checked"
                          size="lg"
                        ></b-form-checkbox>
                      </div>
                    </div>
                    <b-row
                      align-h="end"
                      v-if="answer.type && answer.type === 'SELECTABLE'"
                      class="my-1"
                    >
                      <b-col cols="10">
                        <b-form-select
                          v-model="
                            (answer.selectedChoice ? answer.selectedChoice : {})
                              .value
                          "
                          :options="answer.choices"
                          text-field="name"
                          value-field="value"
                          disabled
                        >
                        </b-form-select>
                      </b-col>
                    </b-row>
                    <div
                      class="row"
                      v-show="
                        answer.description && answer.description.length > 0
                      "
                    >
                      <div class="col-12 mb-3 mx-3">
                        <small>{{ answer.description }}</small>
                      </div>
                    </div>
                    <b-row align-h="end">
                      <div class="col-10">
                        <b-form-textarea
                          v-model="answer.notes"
                          max-rows="8"
                          rows="1"
                          placeholder="Notas"
                          disabled
                        >
                        </b-form-textarea>
                      </div>
                    </b-row>
                  </b-list-group-item>
                </b-list-group>
              </b-tab>
            </b-tabs>
          </b-card>
          <b-row class="my-3">
            <b-col cols="12">
              <b-button-toolbar>
                <b-button-group class="mx-1">
                  <b-button
                    variant="default"
                    @click.stop="backToEntries()"
                    size="sm"
                  >
                    <b-icon-chevron-left scale="1.5" />
                  </b-button>
                </b-button-group>
              </b-button-toolbar>
            </b-col>
          </b-row>
          <template v-slot:overlay>
            <b-spinner ref="spinner" type="grow"></b-spinner>
          </template>
        </b-overlay>
      </main>
    </section>
    <b-modal id="download-artifact-modal" hide-footer centered hide-header>
      <div class="text-center">
        <a :href="currentArtifactUrl" target="_blank" class="text-center">
          Descargar
          <b-icon-link45deg scale="1.5" />
        </a>
        <div>
          <small>La liga puede ser compartida y es válida por 1 día</small>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapMutations, mapState, mapActions, mapGetters } from "vuex";
import moment from "moment";
import { isNil, isEmpty, groupBy, sortBy } from "ramda";
import * as R from "ramda";
import Header from "../Header";
import { GOOGLE_MAPS_API_KEY } from "../store/common";
import GMap from "../maps/GoogleMapLoader";
import GMarker from "../maps/GoogleMapMarker";

moment.locale("es");

const grouper = groupBy((answer) => answer.category);

const sortByTimestamp = sortBy(R.prop("timestamp"));

function transformToGoogleMarker(modifiedAt) {
  const text = `Modificado el ${new Date(
    modifiedAt.timestamp
  ).toLocaleString()}`;
  return {
    id: modifiedAt.timestamp,
    position: { lat: modifiedAt.latitude, lng: modifiedAt.longitude },
    label: text,
    title: text,
  };
}

export default {
  name: "Entry",
  data() {
    return {
      loading: true,
      mapApiKey: GOOGLE_MAPS_API_KEY,
      selectedTab: 0,
      currentArtifactUrl: "",
    };
  },
  components: {
    Header,
    GMap,
    GMarker,
  },
  async created() {
    await this.refreshEntry();
    if (isEmpty(this.entry)) {
      const { entryId } = this.$route.params;
      await this.$bvModal.msgBoxOk(
        `La entrada "${entryId}" no existe o fue borrada por un administrador`,
        {
          size: "sm",
          buttonSize: "sm",
          centered: true,
          footerClass: "p-2 border-top-0",
        }
      );
    }
  },
  computed: {
    ...mapState("checklists", ["entry"]),
    ...mapGetters("auth", ["isAdmin"]),
    mapConfig() {
      return {
        zoom: 12,
        center: this.mapMarkers[0] ? this.mapMarkers[0].position : undefined,
      };
    },
    mapMarkers() {
      if (!this.entry || !this.entry.modifiedAt) return [];
      return sortByTimestamp(this.entry.modifiedAt).map((m) =>
        transformToGoogleMarker(m)
      );
    },
    categorizedQuestions() {
      if (!this.entry || !this.entry.answers) return {};
      const groups = grouper(this.entry.answers);
      return groups;
    },
    entryTemplateRevision() {
      if (this.entry && this.entry.templateId)
        return this.entry.templateId.split(".")[1];
      return "?";
    },
    entryTemplateId() {
      if (this.entry && this.entry.templateId)
        return this.entry.templateId.split(".")[0];
      return "?";
    },
  },
  methods: {
    ...mapActions("checklists", [
      "deleteEntry",
      "fetchCheckListEntry",
      "fetchCheckListEntryImages",
      "setEntryCompleteState",
    ]),
    ...mapActions("exporter", ["createExport"]),
    ...mapMutations("checklists", { resetEntry: "RESET_ENTRY" }),
    backToEntries() {
      this.resetEntry();
      const { templateId, rev: revision } = this.$route.params;
      window.history.length > 1
        ? this.$router.go(-1)
        : this.$router.push(`/checklists/${templateId}/${revision}/entries`);
    },
    calculateDuration(entry) {
      const createdOn = moment(entry.createdOn);
      const lastUpdatedAt = moment(entry.lastUpdatedAt);

      return moment
        .duration(lastUpdatedAt.diff(createdOn, "hours", true), "hours")
        .humanize();
    },
    async refreshEntry() {
      this.loading = true;
      const { templateId, rev: revision, entryId } = this.$route.params;
      await this.fetchCheckListEntry({ templateId, revision, entryId });
      this.loading = false;
      if (!isNil(this.entry.images) && !isEmpty(this.entry.images)) {
        await this.fetchCheckListEntryImages({ templateId, revision, entryId });
      }
    },
    async triggerDownloadAction(extension = "pdf") {
      this.loading = true;
      try {
        const exports = await this.createExport({
          entryId: this.entry.entryId,
        });
        this.currentArtifactUrl = exports[extension][0].url;
        await this.$bvModal.show("download-artifact-modal");
      } catch (error) {
        await this.$bvModal.msgBoxOk(
          `Hubo un problema al generar el reporte, por favor de contactar al administrador del sistema, ${error}`,
          {
            size: "sm",
            buttonSize: "sm",
            centered: true,
            footerClass: "p-2 border-top-0",
          }
        );
      } finally {
        this.loading = false;
      }
    },
    async confirmDelete() {
      const confirmation = await this.$bvModal.msgBoxConfirm(
        "¿Esta seguro que desea borrar la entrada?",
        {
          size: "sm",
          buttonSize: "sm",
          okTitle: "Eliminar",
          okVariant: "danger",
          cancelTitle: "Cancelar",
          centered: true,
          footerClass: "p-2 border-top-0",
        }
      );

      if (confirmation) {
        await this.deleteEntry({ entry: this.entry });
        this.backToEntries();
      }
    },
    async confirmCompleteChange() {
      const entryState = this.entry.completed ? "incompleta" : "completada";
      const confirmation = await this.$bvModal.msgBoxConfirm(
        `¿Esta seguro que desea cambiar el estado de la entrada a "${entryState}"?`,
        {
          size: "sm",
          buttonSize: "sm",
          okTitle: "cambiar",
          okVariant: this.entry.completed ? "warning" : "success",
          cancelTitle: "Cancelar",
          centered: true,
          footerClass: "p-2 border-top-0",
        }
      );

      if (confirmation) {
        await this.setEntryCompleteState({
          entry: this.entry,
          completed: !this.entry.completed,
        });
        await this.refreshEntry();
      }
    },
  },
  filters: {
    timestamp(value) {
      return moment(value).format("dddd Do MMM YYYY, hh:mm:ss a");
    },
  },
};
</script>
